import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators';

import { StateController } from '../helpers/state/state-management/state-controller';
import challengeData from '../helpers/state/challenge-data';
import { getQueryStringParameters } from '../helpers/urls';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

/**
 * Challenge Photo Feed View
 * 
 * Uses challenge-photo-feed component to display photos
 * Accessed via view=challenge-photo-feed parameter
 */
@customElement('challenge-photo-feed-view-page')
export class ChallengePhotoFeedViewPage extends LitElement {
    amplitude_callsite: string = 'Challenge Photo Feed View Page';

    @state()
    private challengeId?: string;

    @state()
    private columns: number = 3;

    @state()
    private errorMessage?: string;

    private readonly bindChallengeState = new StateController(this, challengeData);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        const queryString = getQueryStringParameters();
        this.challengeId = queryString.get('challenge_id') ?? undefined;
        const topic_key = queryString.get('topic_key') ?? undefined;
        const topic_id = queryString.get('topic_id') ?? undefined;

        // Parse optional parameters
        const columnsStr = queryString.get('columns');
        
        if (columnsStr) {
            const cols = parseInt(columnsStr, 10);
            if (!isNaN(cols) && cols >= 1 && cols <= 4) {
                this.columns = cols;
            }
        }

        if (!this.challengeId) {
            this.errorMessage = 'No challenge_id provided in URL parameters.';
            return;
        }

        if (!topicData.isReady) {
            await topicData.instantiateTopicData('leaderboard-view', false, false, true, topic_id, topic_key);
            if (!topicData.topic) {
                this.errorMessage = 'Failed to load topic data. Please check your topic_id and topic_key.';
                return;
            }
            if (!pointCache.isReady) {
                await pointCache.instantiatePointCache(topicData.topic, topic_key ?? '');
            }
            if (!challengeData.isReady) {
                await challengeData.instantiateChallengeData(this.challengeId, topic_key);
            }
        }
        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    render() {
        if (!challengeData.isReady) {
                return html`
                <div class="loading">
                    <loading-element text="Loading data..."></loading-element>
                </div>
            `;
        }
        return html`
            <div class="page-container">
                <challenge-photo-feed
                    style="overflow: auto;flex: 1;padding: 1rem;"
                    .errorMessage=${this.errorMessage}
                ></challenge-photo-feed>
            </div>
        `;
    }

    static styles = css`
        :host {
            display: block;
            background: #f8f9fa;
            min-height: 100vh;
        }

        .page-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            height: 100%;
        }

        .photo-feed-header {
            background: white;
            color: var(--main-primary-color, #333);
            padding: 2rem;
            text-align: center;
        }

        .photo-feed-header h2 {
            margin: 0;
            font-size: 2rem;
            font-weight: 600;
        }

        @media (max-width: 768px) {
            .photo-feed-header {
                padding: 1.5rem;
            }

            .photo-feed-header h2 {
                font-size: 1.5rem;
            }
        }
    `;
}
